'use client';
import React, { useEffect, useRef, useState } from 'react';

interface Stat {
  value: string;
  suffix: string;
  label: string;
}

const stats: Stat[] = [
  { value: '40', suffix: '+', label: 'Projects Completed' },
  { value: '2000', suffix: '+', label: 'Cars Parked' },
  { value: '30', suffix: '+', label: 'Developer Partners' },
  { value: '10', suffix: '+', label: 'Years Experience' },
];

function CountUp({ target, suffix, active }: { target: number; suffix: string; active: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start = 0;
    const duration = 2000;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [active, target]);

  return <>{count.toLocaleString()}{suffix}</>;
}

export default function StatsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="py-16 px-4 md:px-8"
      style={{ backgroundColor: '#ffffff', fontFamily: 'Ubuntu, sans-serif' }}
    >
      <div className="max-w-[1200px] mx-auto" ref={ref}>
        {/* Wide rounded card */}
        <div
          className="rounded-[24px] px-6 md:px-10 py-10 md:py-12 flex flex-col md:flex-row items-center gap-8 md:gap-10"
          style={{ backgroundColor: '#F4F6FB' }}
        >
          {/* Left: Title + Subtitle */}
          <div className="w-full md:w-[260px] flex-shrink-0 text-center md:text-left">
            <h2
              className="font-bold text-[#062543] mb-3"
              style={{ fontSize: '32px', lineHeight: '40px' }}
            >
              Our Track Record
            </h2>
            <p
              className="text-gray-500"
              style={{ fontSize: '15px', lineHeight: '24px' }}
            >
              A decade-plus of expertise delivering smart parking solutions across India and abroad.
            </p>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px self-stretch bg-gray-300 mx-4" />

          {/* Right: Stats */}
          <div className="flex-1 w-full grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => (
              <div
                key={i}
                className={`flex flex-col items-center md:items-start transition-all duration-500 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div
                  className="font-bold mb-1"
                  style={{ fontSize: '38px', lineHeight: '46px', color: '#00AAE2' }}
                >
                  <CountUp
                    target={parseInt(stat.value)}
                    suffix={stat.suffix}
                    active={visible}
                  />
                </div>
                <div
                  className="text-gray-600 text-center md:text-left"
                  style={{ fontSize: '14px', lineHeight: '22px' }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
